// Allocation across asset classes, sectors and regions, plus concentration warnings.

export const CLASSES = ["Stocks", "Bonds", "Cash", "Property", "Crypto", "Retirement", "Other"];
export const HOLDING_CLASSES = ["Stocks", "Bonds", "Property", "Other"]; // what a holding can be set to (crypto follows its type)
export const CLASS_COLOR = { Stocks: "#5B7FB5", Bonds: "#3F8368", Cash: "#D9B26B", Property: "#A9793B", Crypto: "#8F6BAE", Retirement: "#7E97A6", Other: "#8C8579" };
export const SECTORS = ["Technology", "Financials", "Health care", "Consumer", "Energy", "Industrials", "Materials", "Real estate", "Utilities", "Communication", "Broad market", "Other"];
export const REGIONS = ["United States", "Australia", "New Zealand", "Canada", "United Kingdom", "Europe", "Japan", "Asia (other)", "Emerging markets", "Global"];
export const DEFAULT_LIMITS = { maxHolding: 20, maxAsset: 35, maxSector: 40, maxRegion: 75 };
const PALETTE = ["#5B7FB5", "#3F8368", "#D9B26B", "#8F6BAE", "#A9793B", "#7E97A6", "#B5445F", "#6FAE8E", "#4A6FA5", "#C9A27A", "#8C8579", "#9B8CC4"];
export const colorFor = (label, list) => { const i = list.indexOf(label); return i >= 0 ? PALETTE[i % PALETTE.length] : "#8C8579"; };

const SUFFIX_REGION = { AX: "Australia", NZ: "New Zealand", TO: "Canada", V: "Canada", L: "United Kingdom", DE: "Europe", PA: "Europe", AS: "Europe", MI: "Europe", MC: "Europe", SI: "Asia (other)", HK: "Asia (other)", T: "Japan", NS: "Emerging markets", BO: "Emerging markets" };
export function guessRegion(symbol, assetType) {
  if (assetType === "crypto") return "Global";
  const m = /\.([A-Z]{1,2})$/.exec(String(symbol || "").toUpperCase());
  return (m && SUFFIX_REGION[m[1]]) || "United States";
}

// Turns holdings (already summarised) and manual accounts into flat { label, value, cls, sector, region } items.
export function buildItems(holdings, accounts) {
  const items = [];
  for (const h of holdings) {
    if (!(h.sum.value > 0)) continue;
    const crypto = h.assetType === "crypto";
    items.push({
      id: h.id, label: h.symbol, value: h.sum.value, kind: "holding",
      cls: crypto ? "Crypto" : h.assetClass || "Stocks",
      sector: h.sector || (crypto ? "Other" : "Unclassified"),
      region: h.region || guessRegion(h.symbol, h.assetType),
    });
  }
  for (const a of accounts || []) {
    if (!(a.value > 0)) continue;
    const cls = a.assetClass || (a.kind === "property" ? "Property" : a.kind === "retirement" ? "Retirement" : a.kind === "cash" || a.kind === "bank" ? "Cash" : "Other");
    items.push({ id: a.id, label: a.name || a.kind, value: a.value, kind: a.kind, cls });
  }
  return items;
}

export function groupBy(items, field, order) {
  const map = {};
  items.forEach((i) => { const k = i[field] || "Unclassified"; map[k] = (map[k] || 0) + i.value; });
  const total = Object.values(map).reduce((s, v) => s + v, 0);
  return Object.entries(map).map(([label, value]) => ({ label, value, pct: total > 0 ? (value / total) * 100 : 0 }))
    .sort((a, b) => b.value - a.value)
    .map((r) => ({ ...r, color: order === CLASSES ? CLASS_COLOR[r.label] || "#8C8579" : colorFor(r.label, order || []) }));
}

// A broad-market fund is diversified by itself, so it is not counted as a concentrated bet.
const diversified = (i) => i.sector === "Broad market";

export function concentration(items, limits = DEFAULT_LIMITS) {
  const L = { ...DEFAULT_LIMITS, ...limits };
  const out = [];
  const totalAssets = items.reduce((s, i) => s + i.value, 0);
  const holdings = items.filter((i) => i.kind === "holding");
  const invested = holdings.reduce((s, i) => s + i.value, 0);

  // 1. One thing carrying too much of everything you own (cash and retirement funds are spread out by nature, so skipped).
  const single = items.filter((i) => i.cls !== "Cash" && i.cls !== "Retirement" && !diversified(i)).sort((a, b) => b.value - a.value)[0];
  if (single && totalAssets > 0) {
    const pct = (single.value / totalAssets) * 100;
    if (pct >= L.maxAsset) out.push({ id: "asset:" + single.id, pct, title: `${single.label} is ${pct.toFixed(0)}% of your total assets`,
      detail: single.cls === "Property" ? "That's common for homeowners, but it means one market drives most of your wealth." : "One asset moving against you would hit your whole position hard. Spreading across assets reduces that." });
  }
  // 2. One holding dominating the investment portfolio.
  const top = holdings.filter((i) => !diversified(i)).sort((a, b) => b.value - a.value)[0];
  if (top && invested > 0) {
    const pct = (top.value / invested) * 100;
    const alreadyFlagged = out.some((w) => w.id === "asset:" + top.id);
    if (pct >= L.maxHolding && !alreadyFlagged) out.push({ id: "holding:" + top.id, pct, title: `${top.label} is ${pct.toFixed(0)}% of your investments`, detail: `Your limit is ${L.maxHolding}%. Trimming it or adding other holdings would lower the risk.` });
  }
  // 3. Sector and 4. region (across investments only).
  const sec = groupBy(holdings.filter((i) => i.sector !== "Broad market" && i.sector !== "Unclassified"), "sector", SECTORS)[0];
  if (sec && sec.pct >= L.maxSector) out.push({ id: "sector:" + sec.label, pct: sec.pct, title: `${sec.pct.toFixed(0)}% of your holdings are in ${sec.label}`, detail: "A single sector can fall together. Mixing sectors smooths the ride." });
  const reg = groupBy(holdings.filter((i) => i.region !== "Global"), "region", REGIONS)[0];
  if (reg && invested > 0 && reg.pct >= L.maxRegion) out.push({ id: "region:" + reg.label, pct: reg.pct, title: `${reg.pct.toFixed(0)}% of your investments are in ${reg.label}`, detail: "Holding mostly one country ties your results to one economy and one currency." });
  return out;
}
